import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
} from '@mui/material';

const MENU_PROPS = { MenuProps: { sx: { '&.MuiMenu-root': { zIndex: '10001 !important' } } } };

export const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'default' },
  { value: 'medium', label: 'Medium', color: 'warning' },
  { value: 'high', label: 'High', color: 'error' },
];

const EMPTY = {
  title: '',
  description: '',
  board_list: '',
  parent: '',
  priority: 'medium',
  start_date: '',
  due_date: '',
  assignees: [],
  linked_section: '',
};

// Ids of a task and everything nested under it — none of them can become its parent
const descendantIds = (taskId, tasks) => {
  const childrenOf = {};
  tasks.forEach((t) => {
    if (t.parent) childrenOf[t.parent] = [...(childrenOf[t.parent] || []), t.id];
  });
  const ids = new Set();
  const stack = [taskId];
  while (stack.length) {
    const id = stack.pop();
    if (ids.has(id)) continue;
    ids.add(id);
    stack.push(...(childrenOf[id] || []));
  }
  return ids;
};

/**
 * Create / edit a board task. `task` is null for a new task; `defaults` pre-fills
 * a new task (e.g. { board_list, parent } when adding a subtask).
 */
const BoardTaskDialog = ({
  open, onClose, onSave, onDelete, task, defaults, lists, tasks, members, sections, canDelete,
}) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (task) {
      setForm({
        title: task.title,
        description: task.description || '',
        board_list: task.board_list,
        parent: task.parent || '',
        priority: task.priority,
        start_date: task.start_date || '',
        due_date: task.due_date || '',
        assignees: task.assignees || [],
        linked_section: task.linked_section || '',
      });
    } else {
      setForm({ ...EMPTY, board_list: lists[0]?.id || '', ...defaults });
    }
  }, [open, task, defaults, lists]);

  const parentOptions = useMemo(() => {
    const excluded = task ? descendantIds(task.id, tasks) : new Set();
    return tasks.filter((t) => !excluded.has(t.id));
  }, [task, tasks]);

  const memberById = useMemo(
    () => Object.fromEntries(members.map((m) => [String(m.id), m])),
    [members],
  );

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        ...form,
        title: form.title.trim(),
        parent: form.parent || null,
        linked_section: form.linked_section || null,
        start_date: form.start_date || null,
        due_date: form.due_date || null,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{task ? 'Edit Task' : 'New Task'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField label="Title" fullWidth required autoFocus value={form.title} onChange={set('title')} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Description" fullWidth multiline minRows={3} value={form.description} onChange={set('description')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select label="List" fullWidth value={form.board_list} onChange={set('board_list')} SelectProps={MENU_PROPS}>
              {lists.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select label="Priority" fullWidth value={form.priority} onChange={set('priority')} SelectProps={MENU_PROPS}>
              {PRIORITIES.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField select label="Parent task" fullWidth value={form.parent} onChange={set('parent')} SelectProps={MENU_PROPS}
              helperText="Nest this task under another one">
              <MenuItem value=""><em>None (top level)</em></MenuItem>
              {parentOptions.map((t) => <MenuItem key={t.id} value={t.id}>{t.title}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Start date" type="date" fullWidth value={form.start_date} onChange={set('start_date')} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Due date" type="date" fullWidth value={form.due_date} onChange={set('due_date')} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={members.map((m) => String(m.id))}
              value={form.assignees.map(String)}
              onChange={(e, value) => setForm((prev) => ({ ...prev, assignees: value }))}
              getOptionLabel={(id) => memberById[id]?.name || memberById[id]?.email || 'Unknown'}
              renderInput={(params) => <TextField {...params} label="Assignees" />}
              slotProps={{ popper: { sx: { zIndex: 10001 } } }}
            />
          </Grid>
          {sections.length > 0 && (
            <Grid item xs={12}>
              <TextField select label="Linked section" fullWidth value={form.linked_section} onChange={set('linked_section')} SelectProps={MENU_PROPS}>
                <MenuItem value=""><em>None</em></MenuItem>
                {sections.map((s) => <MenuItem key={s.id} value={s.id}>{s.title}</MenuItem>)}
              </TextField>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Box>
          {task && canDelete && (
            <Button color="error" onClick={() => onDelete(task)}>Delete</Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.title.trim() || !form.board_list}>
            {task ? 'Save' : 'Create'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default BoardTaskDialog;
